import TR from 'utils/TR';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import HBox from 'components/common/HBox';
import Section from 'components/common/Section';
import SortButton from '../../../common/SortButton';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useSort from 'hooks/tables/useSort';
import PagingIndicator from 'components/common/paging/PagingIndicator';
import usePaging from 'hooks/tables/usePaging';
import defaultRenderPrevButtons from 'components/common/paging/defaultRenderPrevButtons';
import defaultRenderNextButtons from 'components/common/paging/defaultRenderNextButtons';
import Dot from 'components/common/Dot';
import DialogButton from 'components/common/DialogButton';
import BloodGlocoseDialog from '../../../dialogs/BloodGlocoseDialog';
import SelectButton from 'components/common/SelectButton';
import useReportMealAnalysis from 'hooks/communication/useReportMealAnalysis';

import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import * as Luxon from 'luxon';
import { luxonClone } from 'utils/time';
import SORT_DESCENDING from 'constants/SORT_DESCENDING';
import SORT_DEFAULT from 'constants/SORT_DEFAULT';
import DAY_ZONE_TYPES from 'constants/DAY_ZONE_TYPES';
import ABNORMAL_GLOCOSE_TYPES from 'constants/ABNORMAL_GLOCOSE_TYPES';
import ABNORMAL_GLOCOSE_COLORS from 'constants/ABNORMAL_GLOCOSE_COLORS';
import WEEK_DAY_COLUMNS from 'constants/WEEK_DAY_COLUMNS';
import ExerciseDialog from 'components/dialogs/ExerciseDialog';
import CHECK_COLORS from 'constants/CHECK_COLORS';
import useSetChartData from 'hooks/app/useSetChartData';
import { numberWithComma } from 'utils/number';
import DetailMealDialog from 'components/dialogs/DetailMealDialog';

const DEFAULT_SORT_LIST = [{ sort_by: '1', order_by: SORT_DESCENDING }];
const PAGE_ROW_CNT = 5;

export default function DietMealAnalysis(props) {
  const {
    category,
    mealData,
    ...rest
  } = props;

  const {
    order: dateOrder,
    onOrderChange: onDateOrderChange,
  } = useSort(false, SORT_DESCENDING);
  const {
    order: highAfterMealOrder,
    onOrderChange: onHighAfterMealOrderChange,
  } = useSort();
  const {
    order: increasingOrder,
    onOrderChange: onIncreasingOrderChange,
  } = useSort();

  const {
    page,
    onPage,
    npage,
    onNPage,
    maxPage,
    onMaxPage
  } = usePaging({
    resetCondition: [],
    defaultMaxPage: 6,
  });

  const [abnormal_bld_sugr_tp_cd, setAbnormalBloodSugarTpCd] = React.useState('2');
  const onAbnormalTypeChange = React.useCallback((e) => {
    setAbnormalBloodSugarTpCd(() => e.target.value);
    onPage(1);
  }, [onPage]);

  const {
    requestReportMealAnalysis,
  } = useReportMealAnalysis();

  const [sort_list, setSortList] = React.useState(() => DEFAULT_SORT_LIST)
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietMealAnalysis.1', dateOrder);
    const additionals = dateOrder === SORT_DEFAULT ? [] : [{ sort_by: '1', order_by: dateOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '1')])
  }, [dateOrder]);
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietMealAnalysis.1', highAfterMealOrder);
    const additionals = highAfterMealOrder === SORT_DEFAULT ? [] : [{ sort_by: '2', order_by: highAfterMealOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '2')])
  }, [highAfterMealOrder]);
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietMealAnalysis.1', increasingOrder);
    const additionals = increasingOrder === SORT_DEFAULT ? [] : [{ sort_by: '3', order_by: increasingOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '3')])
  }, [increasingOrder]);

  const setChartData = useSetChartData('DietMealAnalysisList');
  const [list, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    requestReportMealAnalysis({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      page_row_cnt: PAGE_ROW_CNT,
      abnormal_bld_sugr_tp_cd,
      page_no: npage,
      sort_list,
    }, (success, data) => {
      if (success) {
        setChartData(() => ({ list: data.meal_analysis_list || [] }));
        setList(() => data.meal_analysis_list || []);
        onMaxPage(data.max_page_no || 1);
        onPage(data.page_no || 1);
      } else {
        setChartData(() => ({ list: [] }));
        setList(() => []);
        onMaxPage(1);
        onPage(1);
        onNPage(1);
      }
    });
  }, [abnormal_bld_sugr_tp_cd, npage, onMaxPage, onNPage, onPage, patient_no, period_end, period_start, requestReportMealAnalysis, setChartData, sort_list]);

  return (
    <Section
      {...ex('DietMealAnalysis', rest)}
      {...{
        caption: T('식사별 분석'),
        options: (
          <>
            <HBox full>
              {/* {T('| 이상혈당 발생내역')} */}
            </HBox>
            <div>
              <SelectButton
                {...{
                  onChange: onAbnormalTypeChange,
                  value: abnormal_bld_sugr_tp_cd,
                  options: [{
                    value: '1',
                    label: '전체',
                  }, {
                    value: '2',
                    label: '이상혈당',
                  }, {
                    value: '3',
                    label: '고혈당',
                  }, {
                    value: '5',
                    label: '저혈당',
                  }, {
                    value: '4',
                    label: '변동혈당',
                  }],
                }}
              />
            </div>
          </>
        ),
        direction: DIRECTION_VERTICAL,
      }}
    >
      <table>
        <thead>
          <tr>
            <th>
              <div>
                <div>
                  {T('날짜')}
                </div>
                <div>
                  <SortButton {...{ order: dateOrder, onClick: onDateOrderChange }} />
                </div>
              </div>

            </th>
            <th>
              <div>
                {T('시간대')}
              </div>
            </th>
            <th>
              <div>
                {T('대표음식')}
              </div>
            </th>

            <th>
              <div>
                {T('이상혈당 발생')}
              </div>
            </th>

            <th>
              <div>
                {T('혈당상승폭')}
                <SortButton {...{ order: increasingOrder, onClick: onIncreasingOrderChange }} />
              </div>
            </th>

            <th>
              <div>
                {T('식전혈당')}
              </div>
            </th>
            <th>
              <div>
                {T('최고혈당')}
                <SortButton {...{ order: highAfterMealOrder, onClick: onHighAfterMealOrderChange }} />
              </div>
            </th>

            <th>
              <div>
                {T('최고혈당 도달시간(분)')}
              </div>
            </th>

            <th>
              <div>
                {T('2시간후 혈당')}
              </div>
            </th>

            <th>
              <div>
                {T('식사전후 볼루스(U)')}
              </div>
            </th>

            <th>
              <div>
                {T('식후 활동(보)')}
              </div>
            </th>

          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const {
              time,
              time_zone_tp_cd,
              main_food_nm,
              bldsugr_before_meal,
              postprand2h_max_bldsugr,
              postprand2h_bldsugr,
              postprand_abnrml_bldsugr_min,
              postprand2h_bldsugr_rise_w,
              postprand2h_bldsugr_tp,
              postprand2h_step_cnt,
              postprand2h_insulin_amt,
              bf_af_meal_insulin_amt,
            } = item;

            const luxon = Luxon.DateTime.fromFormat(time, 'yyyyMMddHHmmss');
            const labelDate = luxon.toFormat('yy.MM.dd');
            const labelWeekday = luxonClone(luxon).weekday;
            const labelTime = luxon.toFormat('HH:mm');
            return (
              <tr key={index}>
                <td>
                  <div>
                    <DialogButton
                      {...{ category: 'App' }}
                      {...ex('ExerciseDialogButton')}
                      {...{ label: T('{0}({1}) {2}', labelDate, WEEK_DAY_COLUMNS[labelWeekday - 1], labelTime) }}
                    >
                      <ExerciseDialog
                        {...{
                          luxon,
                        }}
                      />
                    </DialogButton>
                  </div>
                </td>
                <td>
                  <div>
                    {DAY_ZONE_TYPES[+time_zone_tp_cd - 1]}
                  </div>
                </td>
                <td>
                  <div>
                    <DialogButton
                      {...{ category: 'App' }}
                      {...ex('DetailMealDialogButton')}
                      {...{ label: main_food_nm }}
                    >
                      <DetailMealDialog
                        {...{
                          label: main_food_nm,
                          luxon,
                        }} />
                    </DialogButton>
                  </div>
                </td>

                <td>
                  <div>
                    {ABNORMAL_GLOCOSE_TYPES[postprand2h_bldsugr_tp]}<Dot {...{ color: ABNORMAL_GLOCOSE_COLORS[postprand2h_bldsugr_tp] }} />
                  </div>
                </td>

                <td>
                  <div>
                    {postprand2h_bldsugr_rise_w === 0 ? '' : Math.sign(postprand2h_bldsugr_rise_w) < 0 ? '-' : '+'}{Math.abs(postprand2h_bldsugr_rise_w)}
                  </div>
                </td>

                <td>
                  <div {...ex(['BASE_COLOR', CHECK_COLORS('B', bldsugr_before_meal)])}>
                    {bldsugr_before_meal}
                  </div>
                </td>
                <td>
                  <div {...ex(['BASE_COLOR', CHECK_COLORS('B', postprand2h_max_bldsugr)])}>
                    {postprand2h_max_bldsugr}
                  </div>
                </td>

                <td>
                  <div>
                    {postprand_abnrml_bldsugr_min}
                  </div>
                </td>

                <td>
                  <div {...ex(['BASE_COLOR', CHECK_COLORS('B', postprand2h_bldsugr)])}>
                    {postprand2h_bldsugr}
                  </div>
                </td>
                <td>
                  <div>
                    {bf_af_meal_insulin_amt}
                  </div>
                </td>

                <td>
                  <div>
                    {numberWithComma(postprand2h_step_cnt)}
                  </div>
                </td>

              </tr>
            );
          })}
        </tbody>
      </table>
      <PagingIndicator
        {...{
          indicatorCount: 5,
          maxPage,
          page,
          onNPage,
          renderPrevButtons: defaultRenderPrevButtons,
          renderNextButtons: defaultRenderNextButtons,
        }}
      />
    </Section >
  );
}
